import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StepOverrideSnapshot } from './state/step-override-drafts';
import {
  clearStagedStepCopy,
  reconcileStagedStepCopy,
  setStagedStepCopy,
  useStagedStepCopy,
  type IdentityKey,
  type PlayKey,
} from './state/staged-play-config';

/**
 * The batch-scope ("Flow step defaults") typing buffer + staged-store read
 * channel, extracted VERBATIM from `BulkStepDefaultsEditor.tsx:171-313` so the
 * V1 editor and the V2 Glaze `BulkEditor` (`shell-next/rail/BulkEditor.tsx`)
 * drive one implementation instead of two — the same one-model-two-consumers
 * move `useStepOverrideRowModel` makes for the per-step row
 * (plan `2026-09-30-002` D6 / U3 Step 2).
 *
 * Read `BulkStepDefaultsEditor.tsx`'s file header for the full rationale; it
 * remains the canonical prose for WHY this buffer exists. The short version:
 *
 * ── The typing buffer (Key Technical Decision 4) ─────────────────────────
 * Mirrors `BaseCopyEditor.tsx`'s `BASE_COPY_COMMIT_MS` buffer exactly (see
 * that file's own doc comment for the full rationale — the staged store is
 * PERSISTED, so a raw per-keystroke write is a `JSON.stringify` of the whole
 * identity bucket plus `localStorage.setItem`, and the read channel is
 * card-level, so a raw write also re-renders `PlayQueueCard` and every row
 * in it). ONE buffer entry PER STEP (not one for the whole editor, unlike
 * `BaseCopyEditor`'s single subject+body pair) because each step's Subject+
 * Body is independently edited and independently debounced — typing in step
 * 2 must not reset step 1's pending commit.
 *
 * Flushed on the debounce timer, on UNMOUNT (card collapse/rail switch —
 * same as `BaseCopyEditor`), and on BLUR — the last of which is the CALLER's
 * job: wrap the rendered step rows in `<div onBlur={flushAllPending}>` and
 * React's synthetic `blur` bubbling (backed by the native `focusout` event,
 * which DOES bubble) catches a blur from any Subject input or body editor
 * without either consumer having to add an `onBlur` prop to the row
 * component. Both `BulkStepDefaultsEditor` and the V2 `BulkEditor` do this.
 *
 * The buffer carries ZERO authority: it exists only to keep controlled
 * inputs from losing keystrokes while a write is pending. Followers,
 * persistence, and Send all read only the staged store — never the buffer.
 *
 * ── `reconcileStagedStepCopy` lives HERE, not in the consumer ────────────
 * The mount-time self-clearing prune (`BulkStepDefaultsEditor.tsx:171-174`
 * before this extraction) moved into this hook rather than staying in the
 * consumer, because `liveSteps` is the hook's own argument and BOTH
 * consumers need the prune — leaving it caller-side would mean writing it
 * twice and silently losing it if a third consumer forgot. Each consumer
 * therefore calls it EXACTLY ONCE, by calling this hook once. Do not
 * re-add a `reconcileStagedStepCopy` effect in a consumer.
 */

/** One step's buffered-but-not-yet-committed edit. */
interface BufferedStepEdit {
  stepType: string;
  values: StepOverrideSnapshot;
}

/**
 * Batch mode never has a save error to show: the staged store is never
 * PUT/DELETEd against directly — materialization happens inside
 * `EnrollQueueContext.saveStepOverrides`, which writes ordinary per-lead
 * drafts, never a request against the batch entry itself. Hoisted to module
 * scope because `StepOverrideRow` reads it through `useStepOverrideEntry`,
 * which requires a referentially stable function.
 */
const noSaveError = () => null;

/**
 * D24 fix 2's sibling — see `BulkStepDefaultsEditor.tsx`'s header for the full
 * rationale. Independent per step (unlike `BaseCopyEditor`'s single pair),
 * because each step's fields are edited and committed independently.
 */
const BATCH_STEP_COMMIT_MS = 200;

export interface UseBatchStepBufferArgs {
  /** `EnrollQueueContext.baseCopyDraftStore.identity` — the same identity every staged-play-config read/write in this card uses. */
  identity: IdentityKey | null;
  /** `playConfigKey(playTypeId, playInstanceRef)` — this group's play key. */
  playKey: PlayKey;
  /**
   * The flow's CURRENT steps (`useGongFlowStepsForced(flowId).data?.steps`),
   * used only by the mount-time prune above. `undefined` while the forced
   * refetch is in flight or errored → no prune runs (dropping a staged entry
   * on a transient fetch failure would be data loss, not a prune).
   */
  liveSteps: ReadonlyArray<{ step_order: number; step_type: string }> | undefined;
}

export interface BatchStepBuffer {
  /**
   * `StepOverrideEditor`'s `localDraftFor` bound to THIS buffer + staged read.
   * Returns a referentially stable entry per step for a given
   * buffer/staged generation — required by `useStepOverrideEntry`'s
   * `useSyncExternalStore` binding.
   */
  localDraftFor: (stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  /** `StepOverrideEditor`'s `subscribeStep` — a stable no-op at batch scope (see its own comment below). */
  subscribeStep: (stepNumber: number, onChange: () => void) => () => void;
  /** `StepOverrideEditor`'s `saveErrorFor` — always `null` at batch scope (see `noSaveError`). */
  saveErrorFor: (stepNumber: number) => string | null;
  /** `StepOverrideEditor`'s `onChangeOverride` — buffers, then commits to the staged store after 200ms. */
  handleChangeBatchStep: (stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  /** `StepOverrideEditor`'s `onResetOverride` — REMOVES the staged entry (never writes an empty one). */
  handleResetBatchStep: (stepNumber: number, stepType: string) => void;
  /** Land every buffered step now. Wire to the wrapping `<div onBlur={…}>`; unmount already calls it. */
  flushAllPending: () => void;
}

export function useBatchStepBuffer({ identity, playKey, liveSteps }: UseBatchStepBufferArgs): BatchStepBuffer {
  // The narrow per-play subscription (Key Technical Decision 1's read
  // channel) — this displays the CURRENT staged value once nothing is
  // buffered, exactly the "store while staged, derive when clean" rule
  // `BaseCopyEditor` documents for base copy.
  const staged = useStagedStepCopy(identity, playKey);

  // U3 (gong-bulk-step-defaults-bugfix plan, MUST-ADDRESS) — the mount-time
  // self-clearing prune: the caller's `useGongFlowStepsForced` force-refetches
  // LIVE steps on every mount, so this is the one moment a staged entry can be
  // checked against reality. A staged entry for a step Gong-side
  // deleted/renumbered upstream is invisible in the editor's OWN render (it
  // only ever maps over the live steps) but, without this, survives forever in
  // the staged store and later materializes into a PUT the send-time guard
  // rejects — with no rep-visible way to clear it, since there is nothing left
  // to show a "reset" affordance on. Dropping is correct, not an error: the
  // staged value refers to a step that no longer exists.
  useEffect(() => {
    if (!liveSteps) return;
    reconcileStagedStepCopy(identity, playKey, liveSteps);
  }, [liveSteps, identity, playKey]);

  // ── The typing buffer (see the header) ──────────────────────────────────
  const [buffer, setBuffer] = useState<ReadonlyMap<number, BufferedStepEdit>>(new Map());
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const pendingCommitsRef = useRef<Map<number, () => void>>(new Map());
  // StrictMode: reset at the TOP of the mount effect, not only in cleanup —
  // CLAUDE.md "StrictMode-Resilient Effects".
  const mountedRef = useRef(true);

  const flushStep = useCallback((stepNumber: number) => {
    const timer = timersRef.current.get(stepNumber);
    if (timer !== undefined) {
      clearTimeout(timer);
      timersRef.current.delete(stepNumber);
    }
    const commit = pendingCommitsRef.current.get(stepNumber);
    pendingCommitsRef.current.delete(stepNumber);
    commit?.();
  }, []);

  /** Land every buffered step now — blur (via the caller's wrapping div) / unmount / a card-level Send about to fire. */
  const flushAllPending = useCallback(() => {
    for (const stepNumber of Array.from(pendingCommitsRef.current.keys())) {
      flushStep(stepNumber);
    }
  }, [flushStep]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Card collapse, rail switch, grouping switch, dock close — same
      // survival guarantee `BaseCopyEditor` gives base copy.
      flushAllPending();
    };
  }, [flushAllPending]);

  // `localDraftFor`/`subscribeStep` are the mounted `StepOverrideEditor`'s
  // portable contract (its own header, spec §7 rule 4) — this hook supplies
  // them bound to ITS OWN buffer+staged read, not to any shared store.
  // `subscribeStep` is a stable no-op: unlike the context's real per-mount
  // store (which notifies independently of React's render cycle), every value
  // the rows read here changes ONLY through the consuming component's own
  // re-render (a buffer `setState` or a `useStagedStepCopy` notification) —
  // and `useSyncExternalStore` (the read hook's `useStore` binding) already
  // calls `getSnapshot` fresh on every such render, so there is nothing left
  // for a real subscription to add.
  const subscribeStep = useCallback(() => () => {}, []);
  // `useStepOverrideEntry`'s `useSyncExternalStore` binding requires
  // `getSnapshot` (this hook's `localDraftFor`) to return a REFERENTIALLY
  // STABLE value across calls while nothing changed — an inline
  // `{ values: {...} }` literal returned fresh on every call would violate
  // that and produce React's "Maximum update depth exceeded" loop the moment
  // a row re-renders for any reason. Precomputing one Map keyed by step number
  // (rebuilt only when `buffer`/`staged` actually change) means every read for
  // the same generation returns the SAME object.
  const derivedDrafts = useMemo(() => {
    const map = new Map<number, { values: StepOverrideSnapshot | null }>();
    for (const [stepNumber, edit] of buffer) {
      map.set(stepNumber, { values: edit.values });
    }
    if (staged) {
      for (const [key, stagedStep] of Object.entries(staged)) {
        const stepNumber = Number(key);
        if (!map.has(stepNumber)) {
          map.set(stepNumber, { values: { subject_draft: stagedStep.subject, body_draft: stagedStep.body } });
        }
      }
    }
    return map;
  }, [buffer, staged]);
  const localDraftFor = useCallback(
    (stepNumber: number): { values: StepOverrideSnapshot | null } | null => derivedDrafts.get(stepNumber) ?? null,
    [derivedDrafts],
  );

  const handleChangeBatchStep = useCallback(
    (stepNumber: number, stepType: string, values: StepOverrideSnapshot) => {
      setBuffer(prev => {
        const next = new Map(prev);
        next.set(stepNumber, { stepType, values });
        return next;
      });
      const existingTimer = timersRef.current.get(stepNumber);
      if (existingTimer !== undefined) clearTimeout(existingTimer);
      const commit = () => {
        timersRef.current.delete(stepNumber);
        pendingCommitsRef.current.delete(stepNumber);
        // Clearing the buffer hands the step back to the staged store, which
        // now holds this exact value — no flicker, and an EXTERNAL change
        // (cross-tab rehydrate, post-send prune, another surface) shows
        // through again the moment the rep stops typing. Same posture as
        // `BaseCopyEditor.bufferEdit`'s commit closure.
        if (mountedRef.current) {
          setBuffer(prev => {
            if (!prev.has(stepNumber)) return prev;
            const next = new Map(prev);
            next.delete(stepNumber);
            return next;
          });
        }
        setStagedStepCopy(identity, playKey, stepNumber, {
          stepType,
          subject: values.subject_draft,
          body: values.body_draft,
        });
      };
      pendingCommitsRef.current.set(stepNumber, commit);
      timersRef.current.set(stepNumber, setTimeout(commit, BATCH_STEP_COMMIT_MS));
    },
    [identity, playKey],
  );

  /**
   * Resolved Decision 8 — removes the staged entry outright, rather than
   * recording a `values: null` reset intent: there is no persisted batch
   * state to DELETE, so a reset intent has no referent. Removal means every
   * following row's batch rung goes back to `undefined` for free. Also
   * drops any buffered-but-uncommitted edit for that step, so a reset mid-
   * debounce cannot resurrect the just-cleared value a moment later.
   */
  const handleResetBatchStep = useCallback(
    (stepNumber: number, _stepType: string) => {
      const timer = timersRef.current.get(stepNumber);
      if (timer !== undefined) {
        clearTimeout(timer);
        timersRef.current.delete(stepNumber);
      }
      pendingCommitsRef.current.delete(stepNumber);
      setBuffer(prev => {
        if (!prev.has(stepNumber)) return prev;
        const next = new Map(prev);
        next.delete(stepNumber);
        return next;
      });
      clearStagedStepCopy(identity, playKey, stepNumber);
    },
    [identity, playKey],
  );

  return {
    localDraftFor,
    subscribeStep,
    saveErrorFor: noSaveError,
    handleChangeBatchStep,
    handleResetBatchStep,
    flushAllPending,
  };
}
